import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';

// ==================== TYPES (must be defined before cache code) ====================

export type PermissionAction = 'view' | 'create' | 'edit' | 'delete';

export interface ModulePermissions {
  can_view: boolean;
  can_create: boolean;
  can_edit: boolean;
  can_delete: boolean;
}

export interface PermissionsState {
  role: 'owner' | 'admin' | 'member' | null;
  organizationId: string | null;
  permissions: Record<string, ModulePermissions>;
  loading: boolean;
}

const DEFAULT_PERMISSIONS: ModulePermissions = {
  can_view: false,
  can_create: false,
  can_edit: false,
  can_delete: false,
};

// ==================== CACHE HELPERS ====================
const PERM_CACHE_KEY_PREFIX = 'perm_cache_v1:';
const SUPABASE_AUTH_KEY = 'sb-temtxtpbfjdxjzdoihwr-auth-token';

interface PermCacheData {
  role: 'owner' | 'admin' | 'member' | null;
  organizationId: string | null;
  permissions: Record<string, ModulePermissions>;
  cachedAt: number;
}

// Get userId synchronously from Supabase's local storage (before AuthContext loads)
function getStoredUserId(): string | null {
  try {
    const stored = localStorage.getItem(SUPABASE_AUTH_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    return parsed?.user?.id || null;
  } catch {
    return null;
  }
}

function getInitialPermCache(): PermCacheData | null {
  const userId = getStoredUserId();
  if (!userId) return null;
  return readPermCache(userId);
}

function readPermCache(userId: string): PermCacheData | null {
  try {
    const cached = localStorage.getItem(`${PERM_CACHE_KEY_PREFIX}${userId}`);
    if (!cached) return null;
    const data = JSON.parse(cached) as PermCacheData;
    // Cache valid for 24 hours
    if (Date.now() - data.cachedAt > 24 * 60 * 60 * 1000) {
      localStorage.removeItem(`${PERM_CACHE_KEY_PREFIX}${userId}`);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function writePermCache(userId: string, data: Omit<PermCacheData, 'cachedAt'>) {
  try {
    localStorage.setItem(`${PERM_CACHE_KEY_PREFIX}${userId}`, JSON.stringify({
      ...data,
      cachedAt: Date.now(),
    }));
  } catch {
    // Ignore storage errors
  }
}

export function clearPermCache(userId: string) {
  try {
    localStorage.removeItem(`${PERM_CACHE_KEY_PREFIX}${userId}`);
  } catch {
    // Ignore errors
  }
}

export function usePermissions() {
  const { user } = useAuth();
  
  const [state, setState] = useState<PermissionsState>(() => {
    const cached = getInitialPermCache();
    return {
      role: cached?.role ?? null,
      organizationId: cached?.organizationId ?? null,
      permissions: cached?.permissions ?? {},
      loading: !cached,
    };
  });

  const fetchPermissions = useCallback(async () => {
    if (!user) {
      setState({
        role: null,
        organizationId: null,
        permissions: {},
        loading: false,
      });
      return;
    }

    // DON'T reset to loading=true if we have cached data - prevents flickering

    try {
      // Get current member info
      // Use secure view to prevent access to invited_email column
      const { data: memberData, error: memberError } = await supabase
        .from('organization_members_secure')
        .select('id, role, organization_id')
        .eq('user_id', user.id)
        .or('accepted_at.not.is.null,role.eq.owner')
        .single();

      if (memberError || !memberData) {
        console.error('Error fetching member:', memberError);
        setState({
          role: null,
          organizationId: null,
          permissions: {},
          loading: false,
        });
        return;
      }

      const role = memberData.role as 'owner' | 'admin' | 'member';

      // Owner and admin have all permissions
      if (role === 'owner' || role === 'admin') {
        const newState = {
          role,
          organizationId: memberData.organization_id,
          permissions: {}, // Empty means full access for owner/admin
          loading: false,
        };
        setState(newState);
        
        // Save to cache
        writePermCache(user.id, {
          role,
          organizationId: memberData.organization_id,
          permissions: {},
        });
        return;
      }

      // Get permissions for regular member
      const { data: permsData } = await supabase
        .from('member_permissions')
        .select('module, can_view, can_create, can_edit, can_delete')
        .eq('member_id', memberData.id);

      const permissionsMap = (permsData || []).reduce((acc, p) => {
        acc[p.module] = {
          can_view: p.can_view,
          can_create: p.can_create,
          can_edit: p.can_edit,
          can_delete: p.can_delete,
        };
        return acc;
      }, {} as Record<string, ModulePermissions>);

      const newState = {
        role,
        organizationId: memberData.organization_id,
        permissions: permissionsMap,
        loading: false,
      };
      setState(newState);
      
      // Save to cache
      writePermCache(user.id, {
        role,
        organizationId: memberData.organization_id,
        permissions: permissionsMap,
      });

    } catch (error) {
      console.error('Error fetching permissions:', error);
      setState({
        role: null,
        organizationId: null,
        permissions: {},
        loading: false,
      });
    }
  }, [user]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  /**
   * Check if user can perform an action on a module
   */
  const canAccess = useCallback((module: string, action: PermissionAction): boolean => {
    // Owner and admin have full access
    if (state.role === 'owner' || state.role === 'admin') {
      return true;
    }

    // No role means no access
    if (!state.role) {
      return false;
    }

    // Check specific permission
    const modulePerms = state.permissions[module] || DEFAULT_PERMISSIONS;
    
    switch (action) {
      case 'view': return modulePerms.can_view;
      case 'create': return modulePerms.can_create;
      case 'edit': return modulePerms.can_edit;
      case 'delete': return modulePerms.can_delete;
      default: return false;
    }
  }, [state.role, state.permissions]);

  /**
   * Check if user is organization owner
   */
  const isOwner = useMemo(() => state.role === 'owner', [state.role]);

  /**
   * Check if user is admin or owner
   */
  const isAdminOrOwner = useMemo(() => 
    state.role === 'owner' || state.role === 'admin', 
    [state.role]
  );

  /**
   * Get permissions for a specific module
   */
  const getModulePermissions = useCallback((module: string): ModulePermissions => {
    if (state.role === 'owner' || state.role === 'admin') {
      return {
        can_view: true,
        can_create: true,
        can_edit: true,
        can_delete: true,
      };
    }
    return state.permissions[module] || DEFAULT_PERMISSIONS;
  }, [state.role, state.permissions]);

  return {
    role: state.role,
    organizationId: state.organizationId,
    permissions: state.permissions,
    loading: state.loading,
    canAccess,
    isOwner,
    isAdminOrOwner,
    getModulePermissions,
    refresh: fetchPermissions,
  };
}
